import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useData, save } from "../hooks/useData";
import { api, num } from "../lib/api";
import { toast } from "sonner";
import { PageHeader, Button, Modal, Field, Input, Textarea, Select } from "../components/shared";
import { Plus, Trash2 } from "lucide-react";

export const ITEM_TYPES = ["Full Pant", "Half Pant", "Shirt", "T-Shirt", "Other"];

export const sizesFromRatio = (text, total) => {
  const pairs = [...String(text || "").matchAll(/([A-Za-z0-9]+)\s*[:=-]\s*(\d+(?:\.\d+)?)/g)]
    .map((m) => [m[1].toUpperCase(), parseFloat(m[2])]).filter(([, r]) => r > 0);
  const t = Math.round(parseFloat(total) || 0);
  const rs = pairs.reduce((a, [, r]) => a + r, 0);
  if (!pairs.length || t <= 0 || rs <= 0) return [];
  const base = pairs.map(([s, r]) => { const q = (t * r) / rs; return { size: s, qty: Math.floor(q), frac: q - Math.floor(q) }; });
  let left = t - base.reduce((a, b) => a + b.qty, 0);
  [...base].sort((a, b) => b.frac - a.frac).forEach((b) => { if (left > 0) { b.qty += 1; left -= 1; } });
  return base.map(({ size, qty }) => ({ size, qty }));
};

export function RatioPreview({ ratio, total, testid }) {
  const sizes = sizesFromRatio(ratio, total);
  if (!ratio) return null;
  return (
    <p className="text-xs mt-1" data-testid={testid}>
      {sizes.length
        ? <span className="text-slate-500">Pieces per size: {sizes.map((s) => `${s.size} ${num(s.qty)}`).join(" · ")}</span>
      : <span className="text-amber-600">Enter sizes (e.g. S M L XL) and a ratio for each (e.g. 1 2 2 1)</span>}
    </p>
  );
}

// Free-text Size and Ratio boxes, saved exactly as typed
export function SizeRatioFields({ sizes, ratio, onChange, tid, className = "" }) {
  return (
    <div className={`grid grid-cols-2 gap-3 ${className}`}>
      <Field label="Sizes"><Input value={sizes || ""} onChange={(e) => onChange("size_text", e.target.value)} placeholder="e.g. 28-30-32 or S M L XL" data-testid={`${tid}-sizes`} /></Field>
      <Field label="Ratio"><Input value={ratio || ""} onChange={(e) => onChange("size_ratio", e.target.value)} placeholder="e.g. 1:2:2:1" data-testid={`${tid}-ratio`} /></Field>
    </div>
  );
}

const blank = () => ({ production_order_id: "", design_number: "", cutting_date: new Date().toISOString().slice(0, 10), cutting_sheet_number: "",
  item_type: "", lot_number: "", size_text: "", size_ratio: "", total_qty: "", cutting_master: "", remarks: "" });

export default function Cutting() {
  const navigate = useNavigate();
  const { data: orders, reload: reloadOrders } = useData("/production-orders");
  const [form, setForm] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const started = (o) => o.bulk_upload || o.direct_cut || (o.stages || []).some((s) => (s.qty_in || 0) > 0 || (s.qty_pending || 0) > 0);
  const inProgress = (orders || []).filter((o) => o.sent_for_cutting && !started(o));

  const delAwaiting = async (o) => {
    if (!window.confirm(`Remove ${o.design_number} (${o.code}) from Awaiting Cutting? This deletes the order — allowed only because nothing has been cut yet.`)) return;
    const reason = window.prompt("Reason for deletion (optional):") || "Removed from Awaiting Cutting";
    try { await api.delete(`/production-orders/${o.id}?reason=${encodeURIComponent(reason)}`); toast.success("Removed from Awaiting Cutting"); reloadOrders(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  const delAllAwaiting = async () => {
    if (!window.confirm(`Delete ALL ${inProgress.length} awaiting-cutting orders? This removes only orders that have NOT been cut yet — all other data stays untouched.`)) return;
    try { const { data } = await api.post("/production-orders/delete-awaiting", { reason: "Bulk delete awaiting cutting" }); toast.success(`Deleted ${data.deleted} awaiting-cutting order${data.deleted === 1 ? "" : "s"}`); reloadOrders(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  const openReceive = (o) => setForm({ ...blank(), production_order_id: o.id, design_number: o.design_number, item_type: o.item_type || "",
    total_qty: o.total_qty || "", cutting_master: o.cutting_master || "", size_text: o.cutting_sizes || "", size_ratio: o.cutting_ratio || "" });

  const submit = async (e) => {
    e.preventDefault();
    const dn = String(form.design_number || "").trim();
    if (!dn) { toast.error("Enter the design number"); return; }
    if (!String(form.cutting_sheet_number || "").trim()) { toast.error("Cutting sheet number is mandatory"); return; }
    if (!(parseFloat(form.total_qty) > 0)) { toast.error("Enter the cutting quantity"); return; }
    if (!form.production_order_id) {
      const { data: chk } = await api.get("/designs/check", { params: { design_number: dn } });
      if (chk.exists && !window.confirm(`Design ${dn} already exists on lot ${chk.lots.join(", ")}. Design numbers should be unique.\n\nSave anyway?`)) return;
    }
    try {
      await save("post", "/cutting", { ...form, design_number: dn, total_qty: parseFloat(form.total_qty), sizes: [] });
      toast.success("Cutting recorded — lot is now in WIP → Cutting"); setForm(null); reloadOrders();
    } catch { /* save() shows the error */ }
  };

  return (
    <div>
      <PageHeader title="Cutting" subtitle="Record a cutting with design, sheet number, fabric lot, sizes, ratio and quantity — the lot goes straight into WIP">
        <Button onClick={() => setForm(blank())} data-testid="add-cutting-btn"><Plus size={15} /> New Cutting Entry</Button>
      </PageHeader>

      {inProgress.length > 0 && (
        <div className="mb-5">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-semibold text-slate-700">In Progress — Awaiting Cutting ({inProgress.length})</p>
            <Button size="sm" variant="secondary" onClick={delAllAwaiting} data-testid="cut-del-all-awaiting"><Trash2 size={14} /> Delete All Awaiting</Button>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {inProgress.map((o) => (
              <div key={o.id} className="bg-white border border-slate-200 rounded-lg p-3 flex items-center justify-between shadow-sm" data-testid={`cut-inprogress-${o.id}`}>
                <div>
                  <p className="font-semibold text-blue-800">{o.design_number}</p>
                  <p className="text-xs text-slate-500">{o.code} · {o.colour} · {num(o.total_qty)} pcs</p>
                  <p className="text-xs text-slate-400">Master: {o.cutting_master || "-"}</p>
                </div>
                <div className="flex items-center gap-1.5">
                  <Button size="sm" onClick={() => openReceive(o)} data-testid={`cut-receive-${o.id}`}>Receive</Button>
                  <button onClick={() => delAwaiting(o)} className="p-1.5 text-slate-400 hover:text-red-600" title="Delete this awaiting-cutting order" data-testid={`cut-awaiting-del-${o.id}`}><Trash2 size={15} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {inProgress.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-10 text-center text-slate-400" data-testid="cut-empty">
          Recorded cuttings move to <button className="text-blue-700 hover:underline font-medium" onClick={() => navigate("/wip")}>WIP → Cutting</button>, where you can edit or delete them.
        </div>
      )}

      <Modal open={!!form} onClose={() => setForm(null)} title="New Cutting Entry" wide>
        {form && (
          <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Field label="Design Number" required><Input value={form.design_number} onChange={(e) => set("design_number", e.target.value)} disabled={!!form.production_order_id} required placeholder="e.g. D-1234" data-testid="cut-design" /></Field>
            <Field label="Cutting Date" required><Input type="date" value={form.cutting_date} onChange={(e) => set("cutting_date", e.target.value)} required data-testid="cut-date" /></Field>
            <Field label="Cutting Sheet No" required><Input value={form.cutting_sheet_number} onChange={(e) => set("cutting_sheet_number", e.target.value)} placeholder="e.g. CS-118" required data-testid="cut-sheet-no" /></Field>
            <Field label="Item"><Select value={form.item_type} onChange={(e) => set("item_type", e.target.value)} placeholder="Select item" options={ITEM_TYPES} data-testid="cut-item" /></Field>
            <Field label="Fabric Lot No"><Input value={form.lot_number} onChange={(e) => set("lot_number", e.target.value)} placeholder="e.g. LOT-245" data-testid="cut-lot-no" /></Field>
            <Field label="Cutting Quantity (Pcs)" required><Input type="number" step="1" min="1" value={form.total_qty} onChange={(e) => set("total_qty", e.target.value)} required data-testid="cut-qty" /></Field>
            <SizeRatioFields className="sm:col-span-2" sizes={form.size_text} ratio={form.size_ratio} onChange={set} tid="cut" />
            <Field label="Master Name"><Input value={form.cutting_master} onChange={(e) => set("cutting_master", e.target.value)} data-testid="cut-master" /></Field>
            <Field label="Remarks" className="sm:col-span-3"><Textarea value={form.remarks} onChange={(e) => set("remarks", e.target.value)} data-testid="cut-remarks" /></Field>
            <div className="sm:col-span-3 flex justify-end gap-2 pt-2">
              <Button type="button" variant="secondary" onClick={() => setForm(null)}>Cancel</Button>
              <Button type="submit" data-testid="cut-save">Record Cutting</Button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}
